'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import { List } from '@/components/molecules';
import { usePlayAll } from '@/lib/hooks/PlayAll/usePlayAll';
import { logger } from '@/lib/logger';
import { getRandomDefaultImage } from '@/lib/utils/getRandomImage';
import { cn } from '@/lib/utils/tailwind';

import { Item, ItemPrefix, ItemSuffix, Content } from './TrackListComponents';
import { ContentTypeVariations } from '../CardCarousel';

import type {
  Album,
  FavoritesForController,
  NewsItem,
  PersonalPlaylist,
  Playlist,
  PlaylistTrack,
  RecentsForController,
} from '@/lib/api';

export const TrackList = ({
  mediaType,
  tracks,
  rawData,
}: {
  mediaType: ContentTypeVariations;
  tracks: PlaylistTrack[] | undefined;
  rawData:
    | Album
    | Playlist
    | NewsItem
    | PersonalPlaylist
    | FavoritesForController
    | RecentsForController;
}) => {
  const t = useTranslations();
  const [defaultImage, setDefaultImage] = useState<string | null>(null);

  const { handleClick } = usePlayAll({
    mediaType,
    rawData,
  });
  useEffect(() => {
    setDefaultImage(getRandomDefaultImage());
  }, []);

  const handleAddClick = (trackIndex: number) => {
    logger.debug(`Added track at index: ${trackIndex}`);
  };

  const handleMoreClick = (trackIndex: number) => {
    logger.debug(`More options for track at index: ${trackIndex}`);
  };

  function hasDisplayArtist(
    actors:
      | PlaylistTrack['track_metadata']['actors']
      | Album['tracks'][number]['track_metadata']['actors'],
  ): actors is PlaylistTrack['track_metadata']['actors'] {
    return (
      typeof actors === 'object' &&
      actors !== null &&
      'displayArtist' in actors &&
      typeof actors.displayArtist?.name === 'string'
    );
  }

  const getTopActor = (track: PlaylistTrack, idx: number): string => {
    if (hasDisplayArtist(track?.track_metadata?.actors)) {
      return track.track_metadata.actors.displayArtist.name;
    }
    if (
      'tracks' in rawData &&
      Array.isArray(rawData.tracks) &&
      rawData.tracks[idx] &&
      hasDisplayArtist(rawData.tracks[idx].track_metadata?.actors)
    ) {
      return rawData.tracks[idx].track_metadata.actors.displayArtist.name;
    }
    return track.actors?.[0]?.name || '';
  };

  if (!defaultImage) return null;

  return (
    <List.Root
      type="custom"
      role="list"
      aria-label={t.has('TRACK_LIST_LABEL') ? t('TRACK_LIST_LABEL') : 'Tracks'}
      className={cn('flex flex-col items-center space-y-0')}
    >
      {tracks?.map((track, index) => {
        const actors = track.actors || [];
        const artistTop = getTopActor(track, index);
        const artistBottom =
          actors
            .filter(actor => actor.name !== artistTop)
            .map(actor => actor.name)
            .join(', ') || '';

        const imageUrl =
          track?.track_metadata?.album?.imagePath || defaultImage;

        return (
          <Item
            className={cn('group items-start')}
            key={index}
            prefix={
              <ItemPrefix
                imageUrl={imageUrl}
                title={track.shortName}
                mediaType={mediaType}
                rawData={rawData}
                startIndex={index}
                trackId={track.trackId}
              />
            }
            suffix={
              <ItemSuffix
                id={track.trackId}
                handleAdd={() => handleAddClick(index)}
                handleMore={() => handleMoreClick(index)}
              />
            }
          >
            <Content
              title={track.shortName}
              artistTop={artistTop}
              artistBottom={artistBottom}
              onClick={event =>
                handleClick(
                  event as unknown as React.MouseEvent<HTMLButtonElement>,
                  index,
                  track.trackId,
                )
              }
            />
          </Item>
        );
      })}
    </List.Root>
  );
};
